/**
 * `portal_leave_types.badge_type` doubles as either:
 *   - a canonical class-name vocabulary entry (e.g. `vacation`, `sick`), or
 *   - a literal `#RRGGBB` hex string for admin-defined custom colors.
 *
 * Class-based values use the pre-defined CSS classes (see
 * `portal-absence-badge.css` + `calendar-view.css`). Hex values render with inline
 * styles, computing a contrasting text color from luminance.
 */

const HEX_RE = /^#[0-9a-fA-F]{6}$/

export const isHexColor = (value: string): boolean => HEX_RE.test(value)

/**
 * Return a high-contrast foreground color for a given hex background.
 *
 * Uses the standard relative-luminance formula (per WCAG 2.x). Light
 * backgrounds get a dark text color, dark backgrounds get a light text color.
 */
export const textColorForHex = (hex: string): string => {
    if (!isHexColor(hex)) return '#1a1a2e'
    const r = parseInt(hex.slice(1, 3), 16) / 255
    const g = parseInt(hex.slice(3, 5), 16) / 255
    const b = parseInt(hex.slice(5, 7), 16) / 255
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b
    return luminance > 0.6 ? '#1a1a2e' : '#ffffff'
}

/**
 * Compute inline-style props for a hex badge background. Returns null for
 * non-hex values (caller should fall back to a CSS class).
 */
export const hexBadgeStyle = (value: string): { background: string; color: string } | null => {
    if (!isHexColor(value)) return null
    return { background: value, color: textColorForHex(value) }
}
